'use client';

import { X } from 'lucide-react';
import type { Category, SearchFilters, DifficultyLevel } from '@/types';
import { getDifficultyColor, getDifficultyLabel } from '@/lib/gamification';

interface Props {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  categories: Category[];
  topTags: string[];
  totalAnswered: number;
  totalQuestions: number;
}

const SORT_OPTIONS: { value: SearchFilters['sortBy']; label: string }[] = [
  { value: 'difficulty-asc', label: 'Easiest First' },
  { value: 'difficulty-desc', label: 'Hardest First' },
  { value: 'alphabetical', label: 'A–Z' },
  { value: 'xp-desc', label: 'Most XP' },
  { value: 'newest', label: 'Newest' },
];

export function FilterPanel({
  filters,
  onFiltersChange,
  categories,
  topTags,
  totalAnswered,
  totalQuestions,
}: Props) {
  const toggleCategory = (slug: SearchFilters['categories'][number]) => {
    const next = filters.categories.includes(slug)
      ? filters.categories.filter((c) => c !== slug)
      : [...filters.categories, slug];
    onFiltersChange({ ...filters, categories: next });
  };

  const toggleDifficulty = (d: DifficultyLevel) => {
    const next = filters.difficulties.includes(d)
      ? filters.difficulties.filter((x) => x !== d)
      : [...filters.difficulties, d];
    onFiltersChange({ ...filters, difficulties: next });
  };

  const toggleTag = (tag: string) => {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ ...filters, tags: next });
  };

  const reset = () => {
    onFiltersChange({
      query: '',
      categories: [],
      difficulties: [],
      tags: [],
      sortBy: 'difficulty-asc',
      showAnsweredOnly: false,
      showUnansweredOnly: false,
    });
  };

  const activeCount =
    filters.categories.length +
    filters.difficulties.length +
    filters.tags.length +
    (filters.showAnsweredOnly || filters.showUnansweredOnly ? 1 : 0);

  const progressPct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  return (
    <aside
      className="rounded-2xl p-5 space-y-5 sticky top-24"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        minWidth: 220,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
          Filters {activeCount > 0 && (
            <span
              className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background: 'var(--color-primary)', color: 'white' }}
            >
              {activeCount}
            </span>
          )}
        </h2>
        {activeCount > 0 && (
          <button
            onClick={reset}
            className="text-xs transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Reset all
          </button>
        )}
      </div>

      {/* Progress */}
      <div>
        <div className="flex justify-between text-xs mb-1.5">
          <span style={{ color: 'var(--color-text-secondary)' }}>Progress</span>
          <span style={{ color: '#22C55E' }}>{totalAnswered} / {totalQuestions}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: '#22C55E' }}
          />
        </div>
      </div>

      {/* Sort */}
      <div>
        <label className="block text-xs font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          Sort by
        </label>
        <select
          value={filters.sortBy}
          onChange={(e) => onFiltersChange({ ...filters, sortBy: e.target.value as SearchFilters['sortBy'] })}
          className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
          style={{
            background: 'var(--color-surface-raised)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} style={{ background: '#10101C' }}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Difficulty */}
      <div>
        <p className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          Difficulty
        </p>
        <div className="flex flex-wrap gap-1.5">
          {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((d) => {
            const active = filters.difficulties.includes(d);
            return (
              <button
                key={d}
                onClick={() => toggleDifficulty(d)}
                className="text-xs px-2.5 py-1 rounded-full font-medium transition-all"
                style={{
                  background: active ? `${getDifficultyColor(d)}20` : 'rgba(255,255,255,0.05)',
                  color: active ? getDifficultyColor(d) : 'var(--color-text-muted)',
                  border: `1px solid ${active ? getDifficultyColor(d) : 'transparent'}`,
                }}
              >
                {getDifficultyLabel(d)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Categories */}
      <div>
        <p className="text-xs font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          Categories
        </p>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {categories.map((cat) => {
            const active = filters.categories.includes(cat.slug);
            return (
              <button
                key={cat.slug}
                onClick={() => toggleCategory(cat.slug)}
                className="w-full text-left text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-2 transition-all"
                style={{
                  background: active ? `${cat.color}15` : 'transparent',
                  color: active ? cat.color : 'var(--color-text-secondary)',
                }}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cat.color }} />
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active filter chips */}
      {activeCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filters.categories.map((slug) => {
            const cat = categories.find((c) => c.slug === slug);
            if (!cat) return null;
            return (
              <span
                key={slug}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                style={{ background: `${cat.color}20`, color: cat.color }}
              >
                {cat.name}
                <button onClick={() => toggleCategory(slug)} aria-label={`Remove ${cat.name} filter`}>
                  <X size={10} />
                </button>
              </span>
            );
          })}
          {filters.difficulties.map((d) => (
            <span
              key={d}
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              style={{ background: `${getDifficultyColor(d)}20`, color: getDifficultyColor(d) }}
            >
              {getDifficultyLabel(d)}
              <button onClick={() => toggleDifficulty(d)} aria-label={`Remove ${getDifficultyLabel(d)} filter`}>
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </aside>
  );
}
